/**
 * DebugContext
 * Maneja el estado global del modo debug para soporte técnico.
 * Solo usuarios con códigos en REACT_APP_SUPPORT_EMPLOYEES pueden ver el módulo de soporte.
 */
import React, { createContext, useState, useContext, useCallback, useMemo } from "react";
import { AuthContext } from "../AuthContext/AuthContext";

export const DebugContext = createContext();

// Obtener lista de empleados de soporte desde .env
const getSupportEmployees = () => {
  const envValue = process.env.REACT_APP_SUPPORT_EMPLOYEES || "";
  if (!envValue) return [];
  return envValue.split(",").map((id) => id.trim());
};

export const DebugProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [debugEnabled, setDebugEnabled] = useState(false);

  // Lista de empleados autorizados para soporte
  const supportEmployees = useMemo(() => getSupportEmployees(), []);

  // Verificar si el usuario actual es de soporte
  const isSupportUser = useMemo(() => {
    if (!user?.employee) return false;
    const employeeId = String(user.employee);
    return supportEmployees.includes(employeeId);
  }, [user?.employee, supportEmployees]);

  // Toggle modo debug (solo si es usuario de soporte)
  const toggleDebug = useCallback(() => {
    if (isSupportUser) {
      setDebugEnabled((prev) => !prev);
    }
  }, [isSupportUser]);

  // Activar modo debug
  const enableDebug = useCallback(() => {
    if (isSupportUser) {
      setDebugEnabled(true);
    }
  }, [isSupportUser]);

  // Desactivar modo debug
  const disableDebug = useCallback(() => {
    setDebugEnabled(false);
  }, []);

  const value = useMemo(
    () => ({
      // Estado
      debugEnabled,
      isSupportUser,
      supportEmployees,
      // Acciones
      toggleDebug,
      enableDebug,
      disableDebug,
    }),
    [debugEnabled, isSupportUser, supportEmployees, toggleDebug, enableDebug, disableDebug]
  );

  return (
    <DebugContext.Provider value={value}>
      {children}
    </DebugContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useDebug = () => {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error("useDebug debe usarse dentro de DebugProvider");
  }
  return context;
};

export default DebugContext;
